import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, Search, Bookmark, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { ProductCard, ProductCardSkeleton } from "@/components/ProductCard";
import { EmptyState } from "@/components/States";
import { CONDITIONS, CITIES, UNITS, HARVEST_STATUS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const SORTS = [
  { value: "newest", label: "En Yeni" },
  { value: "price_asc", label: "En Düşük Fiyat" },
  { value: "price_desc", label: "En Yüksek Fiyat" },
  { value: "most_viewed", label: "En Çok Görüntülenen" },
];

export default function Listings({ fixed = {}, heading, subheading, agriculture = false }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [favIds, setFavIds] = useState([]);

  const page = parseInt(params.get("page") || "1", 10);
  const sort = params.get("sort") || "newest";
  const fixedKey = JSON.stringify(fixed);

  const buildQuery = useCallback(() => {
    const p = new URLSearchParams();
    ["q", "category", "city", "condition", "seller_type", "unit", "min_price", "max_price", "harvest_status"].forEach((k) => {
      const v = params.get(k);
      if (v) p.set(k, v);
    });
    p.set("sort", sort);
    p.set("page", String(page));
    p.set("limit", "20");
    Object.entries(JSON.parse(fixedKey)).forEach(([k, v]) => p.set(k, v));
    return p.toString();
  }, [params, sort, page, fixedKey]);

  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data.items)); }, []);
  useEffect(() => {
    if (localStorage.getItem("cc_token")) {
      api.get("/favorites/ids").then(({ data }) => setFavIds(data.ids)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/listings?${buildQuery()}`).then(({ data }) => {
      setItems(data.items); setTotal(data.total); setPages(data.pages);
    }).finally(() => setLoading(false));
  }, [buildQuery]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "" || value === undefined) next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next);
  };

  const clearAll = () => setParams(new URLSearchParams());
  const hasFilters = ["q", "category", "city", "condition", "seller_type", "unit", "min_price", "max_price", "harvest_status"].some((k) => params.get(k));

  const FiltersPanel = (
    <div className="space-y-6">
      {agriculture && (
        <FilterGroup label="Hasat Durumu">
          <Select value={params.get("harvest_status") || "all"} onValueChange={(v) => update("harvest_status", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-harvest"><SelectValue placeholder="Tümü" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Durumlar</SelectItem>
              {HARVEST_STATUS.map((h) => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterGroup>
      )}
      <FilterGroup label="Kategori">
        <Select value={params.get("category") || "all"} onValueChange={(v) => update("category", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Tümü" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Kategoriler</SelectItem>
            {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </FilterGroup>

      <FilterGroup label="Şehir">
        <Select value={params.get("city") || "all"} onValueChange={(v) => update("city", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-city"><SelectValue placeholder="Tümü" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Tüm Şehirler</SelectItem>
            {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </FilterGroup>

      <FilterGroup label="Ürün Durumu">
        <div className="space-y-2">
          {CONDITIONS.map((c) => (
            <label key={c.value} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={params.get("condition") === c.value}
                onCheckedChange={(ch) => update("condition", ch ? c.value : "")}
                data-testid={`filter-condition-${c.value}`} />
              {c.label}
            </label>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup label="Satıcı Tipi">
        <Select value={params.get("seller_type") || "all"} onValueChange={(v) => update("seller_type", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-seller-type"><SelectValue placeholder="Tümü" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="bireysel">Bireysel</SelectItem>
            <SelectItem value="kurumsal">Kurumsal</SelectItem>
          </SelectContent>
        </Select>
      </FilterGroup>

      <FilterGroup label="Birim">
        <Select value={params.get("unit") || "all"} onValueChange={(v) => update("unit", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-unit"><SelectValue placeholder="Tümü" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Tümü</SelectItem>
            {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
          </SelectContent>
        </Select>
      </FilterGroup>

      <FilterGroup label={agriculture ? "Fiyat (₺/kg)" : "Fiyat Aralığı (₺)"}>
        <div className="flex items-center gap-2">
          <Input type="number" placeholder="Min" defaultValue={params.get("min_price") || ""}
            onBlur={(e) => update("min_price", e.target.value)} data-testid="filter-min-price" className="h-9" />
          <span className="text-slate-400">–</span>
          <Input type="number" placeholder="Max" defaultValue={params.get("max_price") || ""}
            onBlur={(e) => update("max_price", e.target.value)} data-testid="filter-max-price" className="h-9" />
        </div>
      </FilterGroup>

      {hasFilters && (
        <Button variant="outline" onClick={clearAll} data-testid="clear-filters" className="w-full gap-1.5">
          <X size={15} /> Filtreleri Temizle
        </Button>
      )}
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 lg:px-6">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">{heading || "Tüm İlanlar"}</h1>
        <p className="mt-1 text-sm text-slate-500">{subheading || `${total} ilan listeleniyor`}</p>
      </div>

      {/* search bar */}
      <div className="mb-6 flex gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input defaultValue={params.get("q") || ""} placeholder="İlan ara..." data-testid="listings-search"
            onKeyDown={(e) => { if (e.key === "Enter") update("q", e.target.value); }}
            className="h-11 pl-10 focus-visible:ring-emerald-500" />
        </div>
        <Select value={sort} onValueChange={(v) => update("sort", v)}>
          <SelectTrigger className="h-11 w-44" data-testid="sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>{SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        <Sheet>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="outline" className="h-11 gap-1.5" data-testid="mobile-filters-trigger"><SlidersHorizontal size={16} /> Filtre</Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-80 overflow-y-auto">
            <SheetHeader><SheetTitle>Filtreler</SheetTitle></SheetHeader>
            <div className="mt-4">{FiltersPanel}</div>
          </SheetContent>
        </Sheet>
        {user && <SaveSearchButton params={params} fixed={fixed} />}
      </div>

      <div className="flex gap-8">
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-32 rounded-xl border border-slate-200 bg-white p-5">{FiltersPanel}</div>
        </aside>

        <div className="flex-1">
          {loading ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => <ProductCardSkeleton key={i} />)}
            </div>
          ) : items.length === 0 ? (
            <EmptyState title="İlan bulunamadı" description="Filtrelerinizi değiştirerek tekrar deneyin." />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                {items.map((l) => <ProductCard key={l.listing_id} listing={l} favored={favIds.includes(l.listing_id)} />)}
              </div>
              {pages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-2">
                  <Button variant="outline" disabled={page <= 1} onClick={() => update("page", String(page - 1))} data-testid="prev-page">Önceki</Button>
                  <span className="px-3 text-sm text-slate-600">{page} / {pages}</span>
                  <Button variant="outline" disabled={page >= pages} onClick={() => update("page", String(page + 1))} data-testid="next-page">Sonraki</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div>
      <Label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">{label}</Label>
      {children}
    </div>
  );
}

function SaveSearchButton({ params, fixed }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const buildQueryObj = () => {
    const obj = { ...fixed };
    ["q", "category", "city", "condition", "seller_type", "unit", "min_price", "max_price", "harvest_status"].forEach((k) => {
      const v = params.get(k);
      if (v) obj[k] = v;
    });
    return obj;
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/saved-searches", { name: name || "Aramam", query: buildQueryObj() });
      toast.success("Arama kaydedildi. Uygun yeni ilanlarda bildirim alacaksınız.");
      setOpen(false);
      setName("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="h-11 gap-1.5" data-testid="save-search-btn"><Bookmark size={16} /><span className="hidden sm:inline">Kaydet</span></Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Aramayı Kaydet</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Mevcut filtrelerinize uyan yeni bir ilan yayınlandığında bildirim alın.</p>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Arama adı (ör. Antalya domates)" data-testid="save-search-name" />
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-search-submit">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
